import { useCallback, useEffect, useState } from 'react';
import type { FormEvent } from 'react';

import { updateProfileName } from '../api';
import type { CurrentUser } from '../api';
import { Logo } from './Logo';
import { t } from '../i18n';
import type { MessageKey } from '../i18n';

/**
 * First-run step. A Google sign-in carries a name, a magic link does not, and
 * the app needs one — for authorship, mentions and "who is in the room".
 */
export function NameStep({
  user,
  onSaved,
}: {
  user: CurrentUser;
  onSaved: (user: CurrentUser) => void;
}): React.ReactNode {
  const [name, setName] = useState(user.name ?? '');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const onSubmit = useCallback(
    async (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      const value = name.trim();
      if (value.length < 2) {
        setError(t('home.name.required'));
        return;
      }
      setBusy(true);
      setError(null);
      try {
        onSaved(await updateProfileName(value));
      } catch {
        setError(t('error.generic'));
      } finally {
        setBusy(false);
      }
    },
    [name, onSaved],
  );

  return (
    <main className="home">
      <section className="home__card">
        <Logo className="login__logo" width={148} />
        <h1 className="login__title">{t('home.name.title')}</h1>
        <p className="login__subtitle">{t('home.name.help')}</p>

        <form className="login__form" onSubmit={(e) => void onSubmit(e)} noValidate>
          <div className="login__field">
            <label className="login__label" htmlFor="profile-name">
              {t('home.name.label')}
            </label>
            <input
              id="profile-name"
              className="login__input"
              name="name"
              autoComplete="name"
              placeholder={t('home.name.placeholder')}
              value={name}
              onChange={(e) => {
                setName(e.currentTarget.value);
                if (error) setError(null);
              }}
              aria-invalid={error ? true : undefined}
              autoFocus
            />
            {error ? (
              <p className="login__error" role="alert">
                {error}
              </p>
            ) : null}
          </div>

          <button className="login__button login__button--primary" type="submit" disabled={busy}>
            {t('home.name.submit')}
          </button>
        </form>
      </section>
    </main>
  );
}

/**
 * Reads `?error=` set by the API when a sign-in was refused, and turns the
 * code into something a person can act on.
 */
export function useSignInError(): MessageKey | null {
  const [message, setMessage] = useState<MessageKey | null>(null);

  useEffect(() => {
    const code = new URLSearchParams(window.location.search).get('error');
    if (!code) return;
    const map: Record<string, MessageKey> = {
      no_access: 'auth.error.no_access',
      sign_in_failed: 'auth.error.sign_in_failed',
      email_unverified: 'auth.error.email_unverified',
    };
    setMessage(map[code] ?? 'error.generic');
    window.history.replaceState({}, '', window.location.pathname);
  }, []);

  return message;
}
